import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import DataTable, { TableColumn, ConditionalStyles } from "react-data-table-component";
import { deleteGroupRI, editGroupRI, getAllGroupRI, saveGroupRI, getAllGroup } from "../api/appliApi";
import FilterComponent from "../components/FilterComponent";
import CustomLoader from "../components/LoaderComponent";
import { customHeader, paginationOptions } from "../utils/StyledDataTable";
import { Form } from "react-bootstrap";
import { FormModal } from "../components/FormModal";
import { MdDeleteForever, MdEdit } from "react-icons/md";
import { ConfirmModal } from "../components/ConfirmModal";
import { ToastContainer, toast } from "react-toastify";
import { TableContainer } from "../utils/StyledComponents";
import "../asset/css/emailInput.css";
import plusCircle from "../asset/img/icon/plus-circle.svg";
import { GroupRIInterface, UserGroupInterface } from "../utils/Type";

const clearFormData = () => ({
    id: "",
    groupe: "",
    client: "",
    groupeAttachement: ""
});

const GroupRI: React.FC = () => {
    const [datas, setDatas] = useState<GroupRIInterface[]>([]);
    const [groupNameData, setGroupNameData] = useState<UserGroupInterface[]>([]);
    const [filterText, setFilterText] = useState('');
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
    const [pending, setPending] = useState(true);
    const [modalShow, setModalShow] = useState(false);
    const [confirmShow, setConfirmShow] = useState(false);
    const [formData, setFormData] = useState(clearFormData());
    const [selectedName, setSelectedName] = useState('');
    const [idToDelete, setIdToDelete] = useState('');
    const submitLockRef = useRef(false);
    const [isSaving, setIsSaving] = useState(false);
    const formRef = useRef<HTMLFormElement|null>(null);

    const fetchData = useCallback(async () => {
        setPending(true);
        try {
            const { data } = await getAllGroupRI();
            setDatas(Array.isArray(data) ? data : []);
        } catch {
            setDatas([]);
        } finally {
            setPending(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
        fetchGroups();
    }, [fetchData]);

    const fetchGroups = async () => {
        try {
            const resp = await getAllGroup();
            if (Array.isArray(resp?.data)) {
                setGroupNameData(resp.data);
            } else {
                setGroupNameData([]);
            }
        } catch (err) {
            setGroupNameData([]);
        }
    };

    const filteredData = useMemo(() => {
        const txt = filterText.toLowerCase();
        return datas.filter(d =>
            d.id.toString().includes(txt) ||
            d.groupe.toLowerCase().includes(txt) ||
            d.client.toLowerCase().includes(txt) ||
            d.groupeAttachement.toLowerCase().includes(txt)
        );
    }, [filterText, datas]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.currentTarget;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleClear = () => { setFilterText(''); setResetPaginationToggle(!resetPaginationToggle); };
    const handleClearForm = () => { setFormData(clearFormData()); setSelectedName(''); };
    const notify = (msg: string) => toast(msg);
    const notifyError = (msg: string) => toast.error(msg);

    const handleValidate = async () => {
        if (submitLockRef.current || isSaving) return;

        submitLockRef.current = true;
        setIsSaving(true);
        try {
            if (formData.id) await editGroupRI.put(formData);
            else await saveGroupRI.post(formData);
            await fetchData();
            handleClearForm();
            notify('Enregistré avec succès !');
            setModalShow(false);
        } catch (err: any) {
            notifyError('Erreur lors de l\'enregistrement.');
        } finally {
            setIsSaving(false);
            submitLockRef.current = false;
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        await handleValidate();
    };

    const handleEdit = (item: GroupRIInterface) => { setFormData(item); setSelectedName(item.groupe); setModalShow(true); };
    const handleShowConfirm = (id: string, name: string) => { setIdToDelete(id); setSelectedName(name); setConfirmShow(true); };
    const handleDelete = async () => {
        try {
            await deleteGroupRI(idToDelete);
            await fetchData(); handleClearForm(); notify('Supprimé avec succès !'); setConfirmShow(false);
        } catch (err: any) { notifyError(err.response?.data?.message || 'Erreur lors de la suppression.'); }
    };

    const columns: TableColumn<GroupRIInterface>[] = [
        { name: 'Id', selector: row => row.id, sortable: true, width: '7%' },
        { name: 'Groupe d\'orgine', selector: row => row.groupe, sortable: true },
        { name: 'Filtre client', selector: row => row.client, sortable: true },
        { name: 'Groupe de destination', selector: row => row.groupeAttachement, sortable: true },
        {
            name: 'Action',
            cell: row => (
                <>
                    <MdEdit size={24} title="Modifier" onClick={() => handleEdit(row)} style={{ cursor: "pointer", marginRight: 8 }} />
                    <MdDeleteForever size={24} title="Supprimer" onClick={() => handleShowConfirm(row.id, row.groupe)} style={{ cursor: "pointer" }} />
                </>
            ),
            sortable: false
        }
    ];

    const conditionalRowStyles: ConditionalStyles<GroupRIInterface>[] = [
        {
            when: row => filteredData.indexOf(row) % 2 !== 0,
            style: { backgroundColor: "#F6F8FD" },
        },
    ];

    return (
        <div className="custom-content">
            <div className="custom-header">
                <span className="custom-title">Gestion d'attribution des RI à un groupe</span>
                <button className="btn-add-circle" onClick={() => setModalShow(true)} disabled={pending}>
                    <span className="btn-text">Ajouter</span>
                    <span className="plus-icon-circle"><img src={plusCircle} alt="icone +" /></span>
                </button>
            </div>

            <ToastContainer />

            <FilterComponent filterText={filterText} onFilter={e => setFilterText(e.target.value)} onClear={handleClear} />

            <TableContainer>
                <DataTable
                    columns={columns}
                    data={filteredData}
                    pagination
                    paginationResetDefaultPage={resetPaginationToggle}
                    conditionalRowStyles={conditionalRowStyles}
                    persistTableHead
                    fixedHeader
                    fixedHeaderScrollHeight="calc(100vh - 150px)"
                    progressPending={pending}
                    progressComponent={<CustomLoader />}
                    customStyles={customHeader}
                    paginationComponentOptions={paginationOptions}
                    noDataComponent={<p style={{ marginTop: 20 }}>Aucun enregistrement trouvé.</p>}
                />
            </TableContainer>

            <FormModal
                show={modalShow}
                onValidate={() => {
                    if (isSaving) return;
                    formRef.current?.requestSubmit();
                }}
                onAbort={() => { setModalShow(false); handleClearForm(); }}
                title={`Détails de la config de règle${selectedName ? ` "${selectedName}"` : ''}`}
                isBusy={isSaving}
                btnValidate={isSaving ? "Enregistrement..." : "Enregistrer"}
                abortDisabled={isSaving}
                body={
                    <Form id="formMail" ref={formRef} onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Groupe d'origine</Form.Label>

                            <Form.Select
                                name="groupe"
                                value={formData.groupe}
                                onChange={handleChange}
                                aria-label="Sélectionner un groupe d'origine"
                                autoFocus
                            >
                                <option value="">-- Sélectionnez un groupe d'origine --</option>
                                {groupNameData.map(g => (
                                    <option key={String((g as any).id)} value={g.name}>
                                        {g.name}{g.codeName ? ` (${g.codeName})` : ''}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Condition d’envoi selon le nom du client</Form.Label>
                            <Form.Control name="client" value={formData.client} onChange={handleChange} placeholder="Client" />
                        </Form.Group>
                        <div className="mb-3 p-2 rounded border bg-light">
                            <div className="fw-semibold mb-2">Légende des règles</div>
                            <ul className="mb-0 ps-3">
                                <li>Champ vide : tous les clients</li>
                                <li>TEST* : clients dont le code commence par TEST</li>
                                <li>*TEST : clients dont le code se termine par TEST</li>
                                <li>*TEST* : clients dont le code contient TEST</li>
                                <li>TEST? : clients commençant par TEST suivi d’un seul caractère</li>
                                <li>TESTxx : clients commençant par TEST suivi d’au moins 2 caractères</li>
                                <li>-TEST* : tous les clients sauf ceux dont le code commence par TEST</li>
                                <li>TEST*; AZER* : clients dont le code commence par TEST ou par AZER</li>
                            </ul>
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label>Groupe de destination</Form.Label>

                            <Form.Select
                                name="groupeAttachement"
                                value={formData.groupeAttachement}
                                onChange={handleChange}
                                aria-label="Sélectionner un groupe de destination"
                            >
                                <option value="">-- Sélectionnez un groupe de destination --</option>
                                {groupNameData.map(g => (
                                    <option key={String((g as any).id)} value={g.name}>
                                        {g.name}{g.codeName ? ` (${g.codeName})` : ''}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Form>
                }
            />

            <ConfirmModal
                show={confirmShow}
                onValidate={handleDelete}
                onAbort={() => setConfirmShow(false)}
                title="Supprimer le groupe d'envoi"
                body={`Êtes-vous sûr de vouloir supprimer "${selectedName}" ?`}
            />
        </div>
    );
};

export default GroupRI;
